import { useEffect, useMemo } from "react";

import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import {
  MEASUREMENT_TYPES_BY_TOOL,
  MEASUREMENT_TOOLSET,
  getDefaultMeasurementTypeForTool,
  getMeasurementToolDefinitions,
  type MeasurementType,
  type ToolType,
} from "./registry";
import type { MeasurementToolsetRailController } from "@/viewport-tools/rail/types";
import { VIEWPORT_TOOLSET_IDS } from "@/viewport-tools/core/contracts";

export interface UseMeasurementToolsetRailControllerArgs {
  scale: MeasurementScale | null;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  onToolChange: (tool: ToolType | null) => void;
  onMeasurementTypeChange: (type: MeasurementType) => void;
  snapEnabled: boolean;
  onSnapToggle: () => void;
  incrementSnapEnabled: boolean;
  onIncrementSnapToggle: () => void;
  incrementStepFeet: number;
  onIncrementStepChange: (stepFeet: number) => void;
  showHeightInput: boolean;
  heightValue: string;
  onHeightChange: (value: string) => void;
  textLocked?: boolean;
  onTextLockToggle?: () => void;
  textFontSize?: number;
  onTextFontSizeChange?: (value: number) => void;
}

export function useMeasurementToolsetRailController({
  scale,
  expanded,
  onExpandedChange,
  activeTool,
  activeMeasurementType,
  onToolChange,
  onMeasurementTypeChange,
  snapEnabled,
  onSnapToggle,
  incrementSnapEnabled,
  onIncrementSnapToggle,
  incrementStepFeet,
  onIncrementStepChange,
  showHeightInput,
  heightValue,
  onHeightChange,
  textLocked,
  onTextLockToggle,
  textFontSize,
  onTextFontSizeChange,
}: UseMeasurementToolsetRailControllerArgs): MeasurementToolsetRailController {
  const measurementTools = useMemo(
    () => getMeasurementToolDefinitions(MEASUREMENT_TOOLSET),
    []
  );

  useEffect(() => {
    if (scale) {
      return;
    }

    if (expanded) {
      onExpandedChange(false);
    }
    if (activeTool !== null) {
      onToolChange(null);
    }
  }, [activeTool, expanded, onExpandedChange, onToolChange, scale]);

  useEffect(() => {
    if (expanded || activeTool === null) {
      return;
    }

    onToolChange(null);
  }, [activeTool, expanded, onToolChange]);

  const selectTool = (tool: ToolType) => {
    if (activeTool === tool) {
      onToolChange(null);
      return;
    }

    onToolChange(tool);
    const measurementTypes = MEASUREMENT_TYPES_BY_TOOL[tool];
    const defaultMeasurementType = getDefaultMeasurementTypeForTool(tool);
    const nextMeasurementType = measurementTypes.includes(defaultMeasurementType)
      ? defaultMeasurementType
      : measurementTypes[0];

    if (activeMeasurementType !== nextMeasurementType) {
      onMeasurementTypeChange(nextMeasurementType);
    }
  };

  return {
    toolsetId: VIEWPORT_TOOLSET_IDS.measurement,
    scale,
    expanded,
    setExpanded: onExpandedChange,
    measurementTools,
    activeTool,
    activeMeasurementType,
    selectTool,
    selectMeasurementType: onMeasurementTypeChange,
    snapEnabled,
    toggleSnap: onSnapToggle,
    incrementSnapEnabled,
    toggleIncrementSnap: onIncrementSnapToggle,
    incrementStepFeet,
    setIncrementStepFeet: onIncrementStepChange,
    showHeightInput,
    heightValue,
    setHeightValue: onHeightChange,
    textLocked,
    toggleTextLock: onTextLockToggle,
    textFontSize,
    setTextFontSize: onTextFontSizeChange,
  };
}
